import baseApi from "app/api";
import api from "api";
import createQueryFn from "../createQueryFn";
import checklistsApi from "./checklists";
import { GetOrdersRequest, GetOrdersResponse, SORT_BY, SORT_DIR } from "api/orders/getOrders";
import {
  CreateCustomerReferenceRequest,
  CreateCustomerReferenceResponse,
} from "api/orders/createCustomerReference";
import { SetFollowUpDoneRequest, SetFollowUpDoneResponse } from "api/orders/setFollowUpDone";
import { GetOrderRequest, GetOrderResponse } from "api/orders/getOrder";
import { AddExternalUserRequest, AddExternalUserResponse } from "api/orders/addExternalUser";
import { EditSubcontractorRequest, EditSubcontractorResponse } from "api/orders/editSubcontractor";
import { GenerateReportRequest, GenerateReportResponse } from "api/orders/generateReport";
import { SimpleOrder } from "models/SimpleOrder";
import {
  RemoveExternalUserRequest,
  RemoveExternalUserResponse,
} from "api/orders/removeExternalUser";
import { SyncToPyramidRequest, SyncToPyramidResponse } from "api/orders/syncToPyramid";
import { UpdateOrderStatusRequest, UpdateOrderStatusResponse } from "api/orders/updateOrderStatus";
import {
  EditCustomerDetailsRequest,
  EditCustomerDetailsResponse,
} from "api/orders/editCustomerDetails";
import { CreateChecklistRequest, CreateChecklistResponse } from "api/checklists/createChecklist";
import { EditReasonRequest, EditReasonResponse } from "api/approve/editReason";
import { ExcludeChecklistRequest, ExcludeChecklistResponse } from "api/checklists/excludeChecklist";
import {
  GetAllIncludedImagesRequest,
  GetAllIncludedImagesResponse,
} from "api/orders/getAllIncludedImages";
import { GetFileRequest, GetFileResponse } from "api/bin/getFile";
import { GetSignatureRequest, GetSignatureResponse } from "api/bin/getSignature";
import { SerialEntity } from "models/Checklist";
import { OrderSerial } from "models/OrderSerial";
import { DeleteCustomerReferenceRequest } from "api/orders/deleteCustomerReference";
import {
  UpdateCustomerReferenceResponse,
  UpdateCustomerReferenceRequest,
} from "api/orders/updateCustomerReference";
import { DeleteOrderSerialRequest, DeleteOrderSerialResponse } from "api/orders/deleteOrderSerial";
import { CreateOrderSerialRequest, CreateOrderSerialResponse } from "api/orders/createOrderSerial";
import { AddChildOrderRequest, AddChildOrderResponse } from "api/orders/addChildOrder";
import { DeleteChildOrderRequest, DeleteChildOrderResponse } from "api/orders/deleteChildOrder";
import { AssignCompanyRequest, AssignCompanyResponse } from "api/orders/assignCompany";
import { UnassignCompanyRequest, UnassignCompanyResponse } from "api/orders/unassignCompany";
import { GetAssignedOrdersRequest, GetAssignedOrdersResponse } from "api/orders/getAssignedOrders";
import { ReviewOrderRequest, ReviewOrderResponse } from "api/orders/reviewOrder";
import { GetOrderCommentsRequest, GetOrderCommentsResponse } from "api/orders/getOrderComments";
import { GetOrderFilesRequest, GetOrderFilesResponse } from "api/orders/getOrderFiles";

const ordersApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ["Order", "OrderComment", "OrderFile"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addExternalUser: builder.mutation<AddExternalUserResponse, AddExternalUserRequest>({
        queryFn: createQueryFn(api.orders.addExternalUser),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      createChecklist: builder.mutation<CreateChecklistResponse, CreateChecklistRequest>({
        queryFn: createQueryFn(api.checklists.createChecklist),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      editCustomerDetails: builder.mutation<
        EditCustomerDetailsResponse,
        EditCustomerDetailsRequest
      >({
        queryFn: createQueryFn(api.orders.editCustomerDetails),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      editSubcontractor: builder.mutation<EditSubcontractorResponse, EditSubcontractorRequest>({
        queryFn: createQueryFn(api.orders.editSubcontractor),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
        onQueryStarted: async ({ orderId, assignedToCompany }, { dispatch, queryFulfilled }) => {
          // Optimistically update the cache to reflect the new assigned company immediately
          const patchResult = dispatch(
            ordersApi.util.updateQueryData("getOrderById", orderId, (draft) => {
              if (!draft) return;
              draft.assignedToCompany = assignedToCompany;
            })
          );
          // If the mutation fails, undo the optimistic update
          await queryFulfilled.catch(() => patchResult.undo());
        },
      }),

      excludeChecklist: builder.mutation<
        ExcludeChecklistResponse,
        ExcludeChecklistRequest & { orderId: string }
      >({
        queryFn: ({ orderId, ...args }) => {
          return createQueryFn(api.checklists.excludeChecklist)(args);
        },
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "Checklist", id: checklistId },
        ],
        onQueryStarted: async (
          { checklistId, excludedFromReport },
          { dispatch, queryFulfilled }
        ) => {
          // Optimistically update the cache
          const patchResult = dispatch(
            checklistsApi.util.updateQueryData("getChecklist", checklistId, (draft) => {
              draft.excludedFromReport = excludedFromReport;
            })
          );
          // Revert the optimistic update on error
          await queryFulfilled.catch(() => patchResult.undo());
        },
      }),

      generateReport: builder.mutation<GenerateReportResponse, GenerateReportRequest>({
        queryFn: createQueryFn(api.orders.generateReport),
      }),

      getOrderById: builder.query<GetOrderResponse, GetOrderRequest>({
        queryFn: createQueryFn(api.orders.getOrder),
        providesTags: (result, error, id) => [{ type: "Order", id }],
      }),

      getOrderComments: builder.query<GetOrderCommentsResponse, GetOrderCommentsRequest>({
        queryFn: createQueryFn(api.orders.getOrderComments),
        providesTags: (result, error, id) => [{ type: "OrderComment", id }],
      }),

      getOrderFiles: builder.query<GetOrderFilesResponse, GetOrderFilesRequest>({
        queryFn: createQueryFn(api.orders.getOrderFiles),
        providesTags: (result, error, id) => [{ type: "OrderFile", id }],
      }),

      getOrders: builder.infiniteQuery<SimpleOrder[], GetOrdersRequest, number>({
        infiniteQueryOptions: {
          initialPageParam: 0,
          getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1,
        },
        query: ({ queryArg, pageParam }) => {
          const { sortBy, sortDir, pageSize, ...filters } = queryArg;
          return {
            url: "order/page",
            method: "POST",
            body: {
              ...filters,
              page: pageParam,
              pageSize: pageSize ?? 50,
              sort: {
                sortBy: !!sortBy ? SORT_BY[sortBy] : null,
                sortType: !!sortDir ? SORT_DIR[sortDir] : null,
              },
            },
          };
        },
        transformResponse: (response: GetOrdersResponse) => response?.orders || [],
        providesTags: () => [{ type: "Order", id: "LIST" }],
      }),

      removeExternalUser: builder.mutation<RemoveExternalUserResponse, RemoveExternalUserRequest>({
        queryFn: createQueryFn(api.orders.removeExternalUser),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      setFollowUpDone: builder.mutation<SetFollowUpDoneResponse, SetFollowUpDoneRequest>({
        queryFn: createQueryFn(api.orders.setFollowUpDone),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      createCustomerReference: builder.mutation<
        CreateCustomerReferenceResponse,
        CreateCustomerReferenceRequest
      >({
        queryFn: createQueryFn(api.orders.createCustomerReference),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      updateCustomerReference: builder.mutation<
        UpdateCustomerReferenceResponse,
        UpdateCustomerReferenceRequest
      >({
        queryFn: createQueryFn(api.orders.updateCustomerReference),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      deleteCustomerReference: builder.mutation<void, DeleteCustomerReferenceRequest>({
        queryFn: createQueryFn(api.orders.deleteCustomerReference),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      syncToPyramid: builder.mutation<SyncToPyramidResponse, SyncToPyramidRequest>({
        queryFn: createQueryFn(api.orders.syncToPyramid),
        invalidatesTags: (result, error, orderId) => [{ type: "Order", id: orderId }],
      }),

      updateNotOnSiteReason: builder.mutation<
        EditReasonResponse,
        EditReasonRequest & { orderId: string }
      >({
        queryFn: ({ orderId, ...args }) => {
          return createQueryFn(api.approve.editReason)(args);
        },
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      updateOrderStatus: builder.mutation<UpdateOrderStatusResponse, UpdateOrderStatusRequest>({
        queryFn: createQueryFn(api.orders.updateOrderStatus),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
      }),

      getAllIncludedImages: builder.query<
        GetAllIncludedImagesResponse,
        GetAllIncludedImagesRequest
      >({
        queryFn: createQueryFn(api.orders.getAllIncludedImages),
      }),

      getFullMedia: builder.query<GetFileResponse, GetFileRequest>({
        queryFn: createQueryFn(api.bin.getFile),
      }),

      getSignatureImage: builder.query<GetSignatureResponse, GetSignatureRequest>({
        queryFn: createQueryFn(api.bin.getSignature),
      }),

      getAllOrderSerials: builder.query<OrderSerial[], GetOrderRequest>({
        queryFn: (args) =>
          api.orders
            .getOrder(args)
            .then((res) => ({ data: res?.productRows.flatMap((pr) => pr.serials) || [] })),
        providesTags: (result, error, orderId) => [{ type: "OrderSerial", id: "LIST" }],
      }),

      getAllSerialEntities: builder.query<SerialEntity[], GetOrderRequest>({
        queryFn: (args) =>
          api.orders
            .getOrder(args)
            .then((res) => ({ data: res?.checklists.flatMap((c) => c.serialEntities) || [] })),
        providesTags: (result, error, orderId) => [{ type: "SerialEntity", id: "LIST" }],
      }),

      deleteOrderSerial: builder.mutation<
        DeleteOrderSerialResponse,
        DeleteOrderSerialRequest & { checklistId?: string; orderId: string }
      >({
        queryFn: createQueryFn(api.orders.deleteOrderSerial),
        invalidatesTags: (result, error, { checklistId, orderId }) => [
          { type: "Order", id: orderId },
          { type: "SerialEntity", id: "LIST" },
          checklistId ? { type: "Checklist", id: checklistId } : null,
        ],
      }),

      createOrderSerial: builder.mutation<
        CreateOrderSerialResponse,
        CreateOrderSerialRequest & { checklistId?: string }
      >({
        queryFn: createQueryFn(({ checklistId, ...args }) => api.orders.createOrderSerial(args)),
        invalidatesTags: (result, error, { orderNo, checklistId }) => [
          { type: "Order", id: orderNo },
          { type: "OrderSerial", id: "LIST" },
          checklistId ? { type: "Checklist", id: checklistId } : null,
        ],
      }),

      addChildOrder: builder.mutation<AddChildOrderResponse, AddChildOrderRequest>({
        queryFn: createQueryFn(({ parentOrderId, childOrderId }) =>
          api.orders.addChildOrder({ parentOrderId, childOrderId })
        ),
        invalidatesTags: (result, error, { parentOrderId, childOrderId }) => [
          { type: "Order", id: parentOrderId },
          { type: "Order", id: childOrderId },
        ],
      }),

      deleteChildOrder: builder.mutation<DeleteChildOrderResponse, DeleteChildOrderRequest>({
        queryFn: createQueryFn(({ parentOrderId, childOrderId }) =>
          api.orders.deleteChildOrder({ parentOrderId, childOrderId })
        ),
        invalidatesTags: (result, error, { parentOrderId, childOrderId }) => [
          { type: "Order", id: parentOrderId },
          { type: "Order", id: childOrderId },
        ],
      }),

      assignCompany: builder.mutation<
        AssignCompanyResponse,
        AssignCompanyRequest & { uecode: string }
      >({
        queryFn: createQueryFn(api.orders.assignCompany),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
        onQueryStarted: async ({ orderId, companyId, uecode }, { dispatch, queryFulfilled }) => {
          // Optimistically update the cache to reflect the new assigned company immediately
          const patchResult = dispatch(
            ordersApi.util.updateQueryData("getOrderById", orderId, (draft) => {
              if (!draft) return;
              draft.additionalCompanies.push({ id: companyId, name: "", uecode });
            })
          );
          // If the mutation fails, undo the optimistic update
          await queryFulfilled.catch(() => patchResult.undo());
        },
      }),

      unassignCompany: builder.mutation<UnassignCompanyResponse, UnassignCompanyRequest>({
        queryFn: createQueryFn(api.orders.unassignCompany),
        invalidatesTags: (result, error, { orderId }) => [{ type: "Order", id: orderId }],
        onQueryStarted: async ({ orderId, companyId }, { dispatch, queryFulfilled }) => {
          // Optimistically update the cache to reflect the unassigned company immediately
          const patchResult = dispatch(
            ordersApi.util.updateQueryData("getOrderById", orderId, (draft) => {
              if (!draft) return;
              draft.additionalCompanies = draft.additionalCompanies.filter(
                (company) => company.id !== companyId
              );
            })
          );
          // If the mutation fails, undo the optimistic update
          await queryFulfilled.catch(() => patchResult.undo());
        },
      }),

      getAssignedOrders: builder.query<GetAssignedOrdersResponse, GetAssignedOrdersRequest | void>({
        queryFn: createQueryFn(api.orders.getAssignedOrders),
        providesTags: (result, error, id) => [{ type: "Order", id: "LIST" }],
      }),

      reviewOrder: builder.mutation<ReviewOrderResponse, ReviewOrderRequest>({
        queryFn: createQueryFn(api.orders.reviewOrder),
        invalidatesTags: (result, error, { orderNr: orderNo }) => [
          { type: "Order", id: orderNo },
          { type: "Order", id: "LIST" },
        ],
      }),
    }),
  });

export const {
  useAddExternalUserMutation,
  useCreateChecklistMutation,
  useEditCustomerDetailsMutation,
  useEditSubcontractorMutation,
  useExcludeChecklistMutation,
  useGenerateReportMutation,
  useGetOrderByIdQuery,
  useGetOrderCommentsQuery,
  useGetOrderFilesQuery,
  useGetOrdersInfiniteQuery,
  useRemoveExternalUserMutation,
  useSetFollowUpDoneMutation,
  useCreateCustomerReferenceMutation,
  useUpdateCustomerReferenceMutation,
  useDeleteCustomerReferenceMutation,
  useSyncToPyramidMutation,
  useUpdateNotOnSiteReasonMutation,
  useUpdateOrderStatusMutation,
  useGetAllIncludedImagesQuery,
  useGetFullMediaQuery,
  useGetSignatureImageQuery,
  useGetAllOrderSerialsQuery,
  useGetAllSerialEntitiesQuery,
  useDeleteOrderSerialMutation,
  useCreateOrderSerialMutation,
  useAddChildOrderMutation,
  useDeleteChildOrderMutation,
  useAssignCompanyMutation,
  useUnassignCompanyMutation,
  useGetAssignedOrdersQuery,
  useReviewOrderMutation,
} = ordersApi;

export default ordersApi;
